import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	useCallback,
} from "react";
import axios from "./axios_config";
import { useUserDataContext } from "./user_data_context";
import { BASE_API_URL } from "./common/endpoints";
import { describeError } from "./common/utils/error_message";

const BibleHistoryContext = createContext(null);
const MAX_HISTORY_LENGTH = 15;

const normalizeRef = ref => {
	if (!ref) return null;
	const ch = typeof ref.ch === "string" ? parseInt(ref.ch, 10) : ref.ch;
	// Optional last-read verse (v). Kept only when past verse 1 — verse 1 is where a
	// chapter opens anyway, so storing it would be noise. The Bible modal ignores
	// the field, and the server stores user_bible_history as an untyped array.
	const v = typeof ref.v === "string" ? parseInt(ref.v, 10) : ref.v;
	const base = {
		b: ref.b ?? null,
		bk: ref.bk ?? null,
		ch: Number.isFinite(ch) ? ch : null,
	};
	return Number.isInteger(v) && v > 1 ? { ...base, v } : base;
};

const isValidRef = r => !!(r?.b && r?.bk && Number.isInteger(r?.ch));
const sameRef = (a, b) => a?.b === b?.b && a?.bk === b?.bk && a?.ch === b?.ch;

function useDebouncedCallback(cb, delay = 300) {
	const t = useRef(null);

	useEffect(() => {
		return () => {
			if (t.current) clearTimeout(t.current);
		};
	}, []);

	return useCallback(
		(...args) => {
			if (t.current) clearTimeout(t.current);
			t.current = setTimeout(() => cb(...args), delay);
		},
		[cb, delay],
	);
}

export function BibleHistoryProvider({ children }) {
	const { userData } = useUserDataContext();

	const [bibleHistory, setBibleHistory] = useState([]);

	const lastSavedRef = useRef(null);

	const userId = userData?._id;

	// Seed from userData (/v1/auth/profile); edits live in local state and persist via patchBibleHistory.
	useEffect(() => {
		if (!userId) {
			setBibleHistory([]);
			return;
		}
		const serverHistory = Array.isArray(userData?.user_bible_history)
			? userData.user_bible_history
			: [];
		setBibleHistory(serverHistory);
	}, [userId]);

	const patchBibleHistory = useCallback(
		async next => {
			if (!userId) return;
			try {
				await axios.patch(`${BASE_API_URL}/v1/users/me`, {
					user_bible_history: next,
				});
			} catch (e) {
				console.error("Error updating bible history:", describeError(e));
			}
		},
		[userId],
	);

	const debouncedPatch = useDebouncedCallback(patchBibleHistory, 300);

	const addBibleHistoryEntry = useCallback(
		entry => {
			const norm = normalizeRef(entry);
			if (!isValidRef(norm)) return;
			if (sameRef(lastSavedRef.current, norm)) return;

			setBibleHistory(prev => {
				const list = prev || [];
				// Re-entering a chapter promotes it back to the top. Carry its saved
				// last-read verse across so a pass-through visit does not wipe it (the
				// new entry has no v of its own until the reader leaves again).
				const existing = list.find(i => i.b === norm.b && i.bk === norm.bk && i.ch === norm.ch);
				const entry =
					existing && existing.v != null && norm.v == null ? { ...norm, v: existing.v } : norm;
				const filtered = list.filter(
					i => !(i.b === norm.b && i.bk === norm.bk && i.ch === norm.ch),
				);
				const next = [entry, ...filtered].slice(0, MAX_HISTORY_LENGTH);
				debouncedPatch(next);
				return next;
			});

			lastSavedRef.current = norm;
		},
		[debouncedPatch],
	);

	// Records the last-read verse onto an existing entry (the chapter the reader just
	// left). Separate from addBibleHistoryEntry because the verse is only known on
	// leaving, not on entering. No-op when that chapter is not in the journal.
	const setEntryVerse = useCallback(
		(ref, verse) => {
			const b = ref?.b ?? null;
			const bk = ref?.bk ?? null;
			const ch = typeof ref?.ch === "string" ? parseInt(ref.ch, 10) : ref?.ch;
			const v = typeof verse === "string" ? parseInt(verse, 10) : verse;
			if (!b || !bk || !Number.isInteger(ch) || !Number.isInteger(v) || v < 1) return;

			setBibleHistory(prev => {
				const list = prev || [];
				let changed = false;
				const next = list.map(i => {
					if (i.b === b && i.bk === bk && i.ch === ch && i.v !== v) {
						changed = true;
						return { ...i, v };
					}
					return i;
				});
				if (!changed) return prev;
				debouncedPatch(next);
				return next;
			});
		},
		[debouncedPatch],
	);

	const value = useMemo(
		() => ({
			bibleHistory,
			addBibleHistoryEntry,
			setEntryVerse,
		}),
		[bibleHistory, addBibleHistoryEntry, setEntryVerse],
	);

	return <BibleHistoryContext.Provider value={value}>{children}</BibleHistoryContext.Provider>;
}

export function useBibleHistoryContext() {
	const ctx = useContext(BibleHistoryContext);
	if (!ctx) throw new Error("useBibleHistoryContext must be used within <BibleHistoryProvider>");
	return ctx; // { bibleHistory, addBibleHistoryEntry }
}

// --- Convenient adapter for auto-recording (optional) ---
/** * useAutoRecordBibleHistory({ b, bk, ch, enabled })
 * automatically calls addBibleHistoryEntry when parameters change */

export function useAutoRecordBibleHistory({ b, bk, ch, enabled = true }) {
	const { addBibleHistoryEntry } = useBibleHistoryContext();
	const ref = React.useMemo(() => normalizeRef({ b, bk, ch }), [b, bk, ch]);

	useEffect(() => {
		if (!enabled) return;
		addBibleHistoryEntry(ref);
	}, [enabled, ref?.b, ref?.bk, ref?.ch, addBibleHistoryEntry]);
}
